import type { SportsbookMeta } from '@/api/affiliateLinks'
import type { Sportsbook } from '@/types/types'
import type { EventResponseData } from '../lib/api-client'
import { useMemo, useState } from 'react'
import NXTbetsLogo from '../assets/nxtbets.svg?react'
import { useRegulatory } from '../hooks/useRegulatory'
import { trackRGExpand } from '../lib/analytics'
import { hasLogo, LogoMark } from './sportsbookLogos'

type SelectionHandler = (
  marketIndex: number,
  selectionIndex: number,
  sportsbook: Sportsbook
) => void

// Brand metadata for the books the widget renders comes from the API
// response (Supabase catalog → marketAggregator → sportsbooks field).
// The widget receives a Record<sportsbookName, SportsbookMeta> as a prop
// and looks up brand_color / secondary_color / logo_url per book at
// render time. SVG-vs-URL logo selection is owned by ./sportsbookLogos.

type SportsbookMetaMap = Record<string, SportsbookMeta>

// Underdog is a pick'em product: its app never shows American odds, only the
// payout multiplier per pick (1.87x), which is the decimal price. Render
// Underdog cells that way regardless of the American/Decimal toggle so the
// number the user sees here is the one they see after clicking through.
export function displayOdds(sportsbook: Sportsbook, odds: { american: string, decimal: string }, oddsFormat: 'american' | 'decimal'): string {
  if (sportsbook === 'Underdog')
    return `${odds.decimal}x`
  return oddsFormat === 'american' ? odds.american : odds.decimal
}


const availableSportsbooks = [
  'FanDuel',
  'Bet365',
  'DraftKings',
  'Polymarket',
  'Stake',
  'Underdog',
] as const

function SportsbookCheckbox({
  sportsbook,
  meta,
  isSelected,
  isDisabled,
  onChange,
}: {
  sportsbook: Sportsbook
  meta: SportsbookMeta | undefined
  isSelected: boolean
  isDisabled: boolean
  onChange: (sportsbook: Sportsbook, checked: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => !isDisabled && onChange(sportsbook, !isSelected)}
      disabled={isDisabled}
      className={`
        relative flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200
        min-w-0 flex-shrink-0
        ${isDisabled
      ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed dark:border-(--ow-hairline) dark:bg-(--ow-inset)'
      : `cursor-pointer hover:scale-105 active:scale-95 ${
        isSelected
          ? 'border-[#0db5ff] bg-blue-50 shadow-md dark:border-(--ow-accent) dark:bg-(--ow-accent-soft) dark:shadow-none'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-(--ow-border) dark:bg-(--ow-inset) dark:hover:border-(--ow-accent) dark:hover:bg-(--ow-inset)'
      }`
    }
      `}
      aria-label={
        isDisabled
          ? `${sportsbook} has no odds for this market`
          : `${isSelected ? 'Unselect' : 'Select'} ${sportsbook}`
      }
      title={isDisabled ? `No ${sportsbook} odds for this market` : undefined}
    >
      {/* Custom checkbox indicator */}
      <div className={`
        w-4 h-4 rounded border-2 flex items-center justify-center transition-colors duration-200
        ${isSelected && !isDisabled
      ? 'bg-[#0db5ff] border-[#0db5ff] dark:bg-(--ow-accent) dark:border-(--ow-accent)'
      : 'bg-white border-gray-300 dark:bg-transparent dark:border-(--ow-border)'
    }
      `}
      >
        {isSelected && !isDisabled && (
          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Logo tile — brand-color square with the logo mark on top, mirrors
          the self-contained tile design the old inline SVGs had baked in.
          Books with `logo_fill` (BetMGM, Caliente, Fanatics, DK Pick6)
          carry the brand color baked into the image, so the tile lets the
          image cover the container edge-to-edge instead of contain-padding. */}
      {hasLogo(sportsbook, meta)
        ? (
            <div
              className={`size-6 flex-shrink-0 rounded overflow-hidden flex items-center justify-center ${meta?.logo_fill ? '' : 'p-0.5'}`}
              style={meta?.brand_color ? { background: meta.brand_color } : undefined}
            >
              <LogoMark sportsbook={sportsbook} meta={meta} className={`w-full h-full ${meta?.logo_fill ? 'object-cover' : 'object-contain'}`} />
            </div>
          )
        : <span className="text-xs font-medium text-gray-700 dark:text-(--ow-ink-2)">{sportsbook}</span>}

    </button>
  )
}

function SelectionRow({
  player,
  marketIndex,
  playerIndex,
  oddsFormat,
  selectedSportsbooks,
  sportsbookMeta,
  onSelectionClick,
}: {
  player: EventResponseData[number]['selections'][number]
  marketIndex: number
  playerIndex: number
  oddsFormat: 'american' | 'decimal'
  selectedSportsbooks: Set<Sportsbook>
  sportsbookMeta: SportsbookMetaMap | undefined
  onSelectionClick: SelectionHandler
}) {
  const filteredSportsbooks = availableSportsbooks
    .filter((sportsbook) => {
      const odds = player.odds[sportsbook]
      return odds && selectedSportsbooks.has(sportsbook)
    })

  return (
    <div className="px-2 @md:pl-6 @md:pr-2 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-bold text-gray-900 text-sm @md:text-base dark:text-(--ow-ink)">
            {player.name}
          </div>
        </div>
        <div className="flex gap-1">
          {filteredSportsbooks.length > 0
            ? filteredSportsbooks.map((sportsbook) => {
                const meta = sportsbookMeta?.[sportsbook]
                const odds = player.odds[sportsbook]
                const oddsValue = displayOdds(sportsbook, odds!, oddsFormat)
                // Polymarket exposes the raw $0–$1 share price alongside its converted
                // American odds. Surface it as a native title tooltip so the unified
                // odds display stays clean while users can still see what Polymarket
                // is "really" quoting (an implied probability share).
                const polymarketPrice = sportsbook === 'Polymarket' && odds && 'price' in odds
                  ? (odds as { price?: string }).price
                  : undefined
                const ariaLabel = sportsbook === 'Polymarket'
                  ? `Trade on Polymarket at ${oddsValue}${polymarketPrice ? ` (share price $${polymarketPrice})` : ''}`
                  : sportsbook === 'Underdog'
                    ? `Make pick with Underdog at ${oddsValue}`
                    : `Place bet with ${sportsbook} at ${oddsValue} odds`

                return (
                  <button
                    key={sportsbook}
                    onClick={() => onSelectionClick(marketIndex, playerIndex, sportsbook)}
                    aria-label={ariaLabel}
                    title={polymarketPrice ? `Polymarket share price: $${polymarketPrice}` : undefined}
                    style={meta?.secondary_color ? { background: meta.secondary_color } : undefined}
                    className="overflow-hidden rounded text-sm font-medium flex flex-col items-center gap-1 pb-1 @md:flex-row @md:p-0 @md:pr-3.5 @md:gap-4 transition-all duration-150 hover:opacity-80 active:scale-95 cursor-pointer bg-gray-100 text-gray-900"
                    type="button"
                  >
                    {hasLogo(sportsbook, meta)
                      ? (
                          <div
                            className={`size-12 md:size-10 flex-shrink-0 overflow-hidden flex items-center justify-center ${meta?.logo_fill ? '' : 'p-1.5'}`}
                            style={meta?.brand_color ? { background: meta.brand_color } : undefined}
                          >
                            <LogoMark sportsbook={sportsbook} meta={meta} className={`w-full h-full ${meta?.logo_fill ? 'object-cover' : 'object-contain'}`} />
                          </div>
                        )
                      : <span className="text-[10px] @md:text-xs font-bold px-2 py-1 bg-white text-gray-900 rounded">{sportsbook}</span>}
                    <span className="font-bold text-[10px] @md:text-base @md:w-16">
                      {oddsValue}
                    </span>
                  </button>
                )
              })
            : (
                <div className="h-[70px] @md:h-10 flex items-center">
                  <span className="text-sm text-gray-400 italic dark:text-(--ow-meta)">No odds available</span>
                </div>
              )}
        </div>
      </div>
    </div>
  )
}

type OverUnderSelection = EventResponseData[number]['selections'][number]

interface OverUnderGroup {
  name: string
  line: string
  over?: { player: OverUnderSelection, index: number }
  under?: { player: OverUnderSelection, index: number }
}

// Over/under markets (`format: 'overUnder'`) arrive as separate Over and
// Under selections per player. Pair them back up (keyed by name + line) so
// each player renders once with both prices; the original selection
// indices are kept so a click resolves to the exact selection the API sent.
export function groupOverUnder(selections: EventResponseData[number]['selections']): OverUnderGroup[] {
  const groups = new Map<string, OverUnderGroup>()
  selections.forEach((player, index) => {
    const line = player.line ?? ''
    const key = `${player.name}\u0000${line}`
    let group = groups.get(key)
    if (!group) {
      group = { name: player.name, line }
      groups.set(key, group)
    }
    if (player.side === 'under')
      group.under = { player, index }
    else
      group.over = { player, index }
  })
  return Array.from(groups.values())
}

// One player, one line, Over + Under prices per book. Mirrors SelectionRow's
// pill (brand tile + odds) but splits the odds cell into two side buttons —
// each click goes through the same handler with that side's own selection
// index, so the bet slip and analytics see the exact Over or Under pick.
function OverUnderRow({
  group,
  marketIndex,
  oddsFormat,
  selectedSportsbooks,
  sportsbookMeta,
  onSelectionClick,
}: {
  group: OverUnderGroup
  marketIndex: number
  oddsFormat: 'american' | 'decimal'
  selectedSportsbooks: Set<Sportsbook>
  sportsbookMeta: SportsbookMetaMap | undefined
  onSelectionClick: SelectionHandler
}) {
  const filteredSportsbooks = availableSportsbooks.filter(sportsbook =>
    selectedSportsbooks.has(sportsbook)
    && (group.over?.player.odds[sportsbook] || group.under?.player.odds[sportsbook]),
  )
  const sides = [
    { key: 'over', label: 'O', word: 'Over', pick: group.over },
    { key: 'under', label: 'U', word: 'Under', pick: group.under },
  ] as const

  return (
    <div className="px-2 @md:pl-6 @md:pr-2 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold text-gray-900 text-sm @md:text-base dark:text-(--ow-ink)">
            {group.name}
          </div>
          <div className="text-xs text-gray-500 dark:text-(--ow-meta)">
            {`O/U ${group.line}`}
          </div>
        </div>
        <div className="flex gap-1">
          {filteredSportsbooks.length > 0
            ? filteredSportsbooks.map((sportsbook) => {
                const meta = sportsbookMeta?.[sportsbook]
                return (
                  <div
                    key={sportsbook}
                    style={meta?.secondary_color ? { background: meta.secondary_color } : undefined}
                    className="overflow-hidden rounded text-sm font-medium flex flex-col items-center gap-1 pb-1 @md:flex-row @md:p-0 @md:pr-2 @md:gap-2 bg-gray-100 text-gray-900"
                  >
                    {hasLogo(sportsbook, meta)
                      ? (
                          <div
                            className={`size-12 md:size-10 flex-shrink-0 overflow-hidden flex items-center justify-center ${meta?.logo_fill ? '' : 'p-1.5'}`}
                            style={meta?.brand_color ? { background: meta.brand_color } : undefined}
                          >
                            <LogoMark sportsbook={sportsbook} meta={meta} className={`w-full h-full ${meta?.logo_fill ? 'object-cover' : 'object-contain'}`} />
                          </div>
                        )
                      : <span className="text-[10px] @md:text-xs font-bold px-2 py-1 bg-white text-gray-900 rounded">{sportsbook}</span>}
                    <div className="flex flex-row gap-1 px-1 @md:px-0">
                      {sides.map(({ key, label, word, pick }) => {
                        const odds = pick?.player.odds[sportsbook]
                        if (!pick || !odds) {
                          return (
                            <span key={key} className="flex flex-col items-center justify-center w-12 @md:w-16 text-[10px] text-gray-400" aria-hidden="true">
                              {label}
                              <span>—</span>
                            </span>
                          )
                        }
                        const oddsValue = displayOdds(sportsbook, odds, oddsFormat)
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => onSelectionClick(marketIndex, pick.index, sportsbook)}
                            aria-label={`${word} ${group.line} ${group.name} with ${sportsbook} at ${oddsValue}${sportsbook === 'Underdog' ? '' : ' odds'}`}
                            className="flex flex-col items-center justify-center w-12 @md:w-16 rounded transition-all duration-150 hover:opacity-80 active:scale-95 cursor-pointer"
                          >
                            <span className="text-[9px] @md:text-[10px] font-semibold uppercase tracking-wide opacity-70">{`${label} ${group.line}`}</span>
                            <span className="font-bold text-[10px] @md:text-base">{oddsValue}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })
            : (
                <div className="h-[70px] @md:h-10 flex items-center">
                  <span className="text-sm text-gray-400 italic dark:text-(--ow-meta)">No odds available</span>
                </div>
              )}
        </div>
      </div>
    </div>
  )
}

// Responsible-gambling disclosure — the widget's last row. The strip always
// reads "21+ Only. Restrictions and eligibility requirements apply."; when at
// least one of the books present in this widget's odds has a regulatory_full
// statement in the catalog (edited on the /affiliate operator pages), a caret
// renders on the right and clicking the strip reveals a scrollable panel
// BELOW it — the strip stays pinned as the header of the expanded box.
// Mirrors the offer cards' caret pattern: no statements → no caret, and the
// text renders pre-line, never parsed as HTML.
function ResponsibleGamblingFooter({
  league,
  presentSportsbooks,
}: {
  league: string
  presentSportsbooks: readonly Sportsbook[]
}) {
  const { data } = useRegulatory()
  const [open, setOpen] = useState(false)

  const statements = useMemo(() => {
    const byName = new Map((data?.books ?? []).map(b => [b.name, b.regulatory_full]))
    return presentSportsbooks
      .filter(book => byName.has(book))
      .map(book => ({ book, text: byName.get(book)! }))
  }, [data, presentSportsbooks])

  const hasStatements = statements.length > 0
  const isOpen = open && hasStatements

  const handleToggle = () => {
    if (!hasStatements)
      return
    if (!open)
      trackRGExpand(league)
    setOpen(!open)
  }

  return (
    <>
      <button
        type="button"
        onClick={handleToggle}
        disabled={!hasStatements}
        aria-expanded={isOpen}
        aria-label={hasStatements
          ? `${isOpen ? 'Hide' : 'Show'} responsible gambling statements`
          : undefined}
        className={`flex-shrink-0 w-full flex items-center justify-center gap-2 border-t border-[#C8E2FF] px-4 py-1.5 @md:px-6 @md:py-2 text-center dark:border-(--ow-hairline) ${
          hasStatements ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-(--ow-inset)' : 'cursor-default'
        }`}
      >
        <span className="text-[8px] @md:text-xs text-gray-400 dark:text-(--ow-meta)">
          21+ Only. Restrictions and eligibility requirements apply.
        </span>
        {hasStatements && (
          <svg
            className={`w-3 h-3 flex-shrink-0 text-gray-400 transition-transform duration-200 dark:text-(--ow-meta) ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>
      {isOpen && (
        <div className="flex-shrink-0 max-h-40 @md:max-h-56 overflow-y-auto border-t border-[#C8E2FF] bg-gray-50 px-4 py-3 @md:px-6 dark:border-(--ow-hairline) dark:bg-(--ow-inset)">
          {statements.map(({ book, text }) => (
            <div key={book} className="mb-3 last:mb-0">
              <div className="text-[10px] @md:text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-(--ow-ink-2)">
                {book}
              </div>
              <p className="mt-1 text-[10px] @md:text-xs leading-relaxed text-gray-500 whitespace-pre-line dark:text-(--ow-meta)">
                {text}
              </p>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

export const H2H_SENTINEL = -1

function Widget({
  league,
  leagueName,
  sportsbookMeta,
  markets,
  selectedMarketIndex,
  onMarketChange,
  oddsFormat,
  onToggleOddsFormat,
  onSelectionClick,
  isH2HMode = false,
  h2hIndices,
  eventName,
}: {
  /** League slug (analytics param on the RG-footer expand event). */
  league: string
  leagueName: string
  sportsbookMeta: SportsbookMetaMap | undefined
  markets: EventResponseData
  selectedMarketIndex: number
  onMarketChange: (marketIndex: number) => void
  oddsFormat: 'american' | 'decimal'
  onToggleOddsFormat: () => void
  onSelectionClick: SelectionHandler
  isH2HMode?: boolean
  h2hIndices?: number[]
  eventName?: string
}) {
  // Sportsbooks that actually have odds in this widget's data — the union
  // across all markets and all selections. Drives both the visible toggle
  // icons and the initial selectedSportsbooks set, so partners only see
  // toggles for sportsbooks that have data for the current event.
  const presentSportsbooks = useMemo(() => {
    const present = new Set<Sportsbook>()
    for (const market of markets) {
      for (const selection of market.selections ?? []) {
        for (const book of Object.keys(selection.odds ?? {}))
          present.add(book as Sportsbook)
      }
    }
    // Preserve the canonical display order from availableSportsbooks.
    return availableSportsbooks.filter(b => present.has(b))
  }, [markets])

  // Initialize with all *present* sportsbooks selected (so toggles light up
  // by default rather than starting blank).
  const [selectedSportsbooks, setSelectedSportsbooks] = useState<Set<Sportsbook>>(
    () => new Set(presentSportsbooks),
  )

  // Books with at least one selection in the *currently visible* view.
  // Drives the disabled state on toggles so the row stays stable across
  // market changes (no layout shift) but still communicates which books
  // have data for the current market. In H2H list mode the whole card is
  // visible, so any present book is active.
  const activeMarketSportsbooks = useMemo(() => {
    const isShowingH2H = selectedMarketIndex === H2H_SENTINEL
    const visibleMarkets = isShowingH2H
      ? (h2hIndices ?? []).map(i => markets[i]).filter(Boolean) as EventResponseData
      : (markets[selectedMarketIndex] ? [markets[selectedMarketIndex]!] : [])
    const active = new Set<Sportsbook>()
    for (const market of visibleMarkets) {
      for (const selection of market.selections ?? []) {
        for (const book of Object.keys(selection.odds ?? {}))
          active.add(book as Sportsbook)
      }
    }
    return active
  }, [markets, selectedMarketIndex, h2hIndices])

  const isShowingH2H = selectedMarketIndex === H2H_SENTINEL
  const widgetData = isShowingH2H ? undefined : markets[selectedMarketIndex]
  const h2hMarkets = h2hIndices ? h2hIndices.map(i => ({ market: markets[i]!, index: i })) : []

  const handleOddsFormatChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const newFormat = event.target.value as 'american' | 'decimal'
    if (newFormat !== oddsFormat) {
      onToggleOddsFormat()
    }
  }

  const handleMarketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMarketIndex = Number.parseInt(event.target.value)
    onMarketChange(newMarketIndex)
  }

  const handleSportsbookChange = (sportsbook: Sportsbook, checked: boolean) => {
    setSelectedSportsbooks((prev) => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(sportsbook)
      }
      else {
        newSet.delete(sportsbook)
      }
      return newSet
    })
  }

  // Check if any sportsbooks are selected
  const hasSelectedSportsbooks = selectedSportsbooks.size > 0

  return (
    <div className="@container bg-white rounded-lg shadow-xl overflow-hidden mx-auto border-[3px] border-[#C8E2FF] h-[500px] md:h-[700px] flex flex-col dark:bg-(--ow-surface) dark:border-(--ow-border) dark:shadow-[0_4px_14px_rgba(0,0,0,0.55)]" style={{ maxWidth: '1440px' }}>
      {/* Fixed Header */}
      <div className="border-b border-[#C8E2FF] pt-2.5 px-4 @md:pt-7 @md:px-7 flex-shrink-0 dark:border-(--ow-hairline)">
        <div className="flex flex-col gap-3 @min-[720px]:flex-row @min-[720px]:items-center @min-[720px]:justify-between @min-[720px]:gap-4">
          <div className="text-center @md:text-left">
            <h2 className={((isH2HMode || isShowingH2H) && !eventName) ? 'text-xl font-bold' : 'text-md text-gray-500 dark:text-(--ow-meta)'}>
              {leagueName}
            </h2>
            {!isH2HMode && !isShowingH2H && widgetData && (
              <div className="text-xl font-bold mt-0.5">
                {widgetData.name}
              </div>
            )}
            {(isH2HMode || isShowingH2H) && eventName && (
              <div className="text-xl font-bold mt-0.5">
                {eventName}
              </div>
            )}
          </div>
          {!isH2HMode && (
            <select
              value={selectedMarketIndex}
              onChange={handleMarketChange}
              className="pl-3 pr-8 py-1 text-sm font-medium bg-white border border-[var(--lp-accent,#0db5ff)] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0db5ff] focus:border-transparent @md:text-base appearance-none bg-no-repeat dark:bg-(--ow-inset) dark:focus:ring-(--ow-accent)"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundPosition: 'right 12px center' }}
            >
              {markets.map((market, index) => {
                // Individual H2H markets are grouped under one "Head to Head"
                // option, emitted at the position of the first H2H entry so the
                // config's insertBefore ordering carries into the dropdown
                // (season-long markets placed after the H2H range stay below it).
                if (h2hIndices?.includes(index)) {
                  if (index !== h2hIndices[0])
                    return null
                  return (
                    <option key="h2h-group" value={H2H_SENTINEL}>Head to Head</option>
                  )
                }
                return (
                  <option key={`${market.name}-${market.marketName}`} value={index}>
                    {market.name !== widgetData?.name ? `${market.name} - ${market.marketName}` : market.marketName}
                  </option>
                )
              })}
            </select>
          )}
        </div>

        {/* Sportsbook Filter */}
        <div className="mt-3">
          <div className="flex items-center gap-2 @md:gap-3 flex-wrap justify-center @md:justify-start">
            {presentSportsbooks.map(sportsbook => (
              <SportsbookCheckbox
                key={sportsbook}
                sportsbook={sportsbook}
                meta={sportsbookMeta?.[sportsbook]}
                isSelected={selectedSportsbooks.has(sportsbook)}
                isDisabled={!activeMarketSportsbooks.has(sportsbook)}
                onChange={handleSportsbookChange}
              />
            ))}
          </div>
        </div>

        <div className="h-3 @md:h-5" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-4">
        {!hasSelectedSportsbooks
          ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500 dark:text-(--ow-meta)">
                  <p className="text-lg font-medium">No sportsbooks selected</p>
                  <p className="text-sm">Please select at least one sportsbook to view odds</p>
                </div>
              </div>
            )
          : (isH2HMode || isShowingH2H)
              ? (
                  <div className="divide-y-1 divide-[#C8E2FF] dark:divide-(--ow-divider)">
                    {(isH2HMode ? markets.map((m, i) => ({ market: m, index: i })) : h2hMarkets).map(({ market, index: marketIndex }) => (
                      <div key={`${market.name}-${market.marketName}`} className="py-3">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 @md:px-6 mb-2 dark:text-(--ow-meta)">
                          {market.name}
                        </div>
                        {market.selections.map((player, playerIndex) => (
                          <SelectionRow
                            key={player.name}
                            player={player}
                            marketIndex={marketIndex}
                            playerIndex={playerIndex}
                            oddsFormat={oddsFormat}
                            selectedSportsbooks={selectedSportsbooks}
                            sportsbookMeta={sportsbookMeta}
                            onSelectionClick={onSelectionClick}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )
              : (
                  <div className="divide-y-1 divide-[#C8E2FF] dark:divide-(--ow-divider)">
                    {widgetData?.format === 'overUnder'
                      ? groupOverUnder(widgetData.selections).map(group => (
                          <OverUnderRow
                            key={`${group.name}-${group.line}`}
                            group={group}
                            marketIndex={selectedMarketIndex}
                            oddsFormat={oddsFormat}
                            selectedSportsbooks={selectedSportsbooks}
                            sportsbookMeta={sportsbookMeta}
                            onSelectionClick={onSelectionClick}
                          />
                        ))
                      : (widgetData?.selections || []).map((player, playerIndex) => (
                          <SelectionRow
                            key={player.name}
                            player={player}
                            marketIndex={selectedMarketIndex}
                            playerIndex={playerIndex}
                            oddsFormat={oddsFormat}
                            selectedSportsbooks={selectedSportsbooks}
                            sportsbookMeta={sportsbookMeta}
                            onSelectionClick={onSelectionClick}
                          />
                        ))}
                  </div>
                )}
      </div>

      <div className="p-4 flex-shrink-0 border-t border-[#C8E2FF] flex items-center justify-between @md:px-6 dark:border-(--ow-hairline)">
        <div className="flex items-center gap-1.5">
          <span className="text-[8px] @md:text-xs text-gray-400 dark:text-(--ow-meta)">Powered by</span>
          <NXTbetsLogo className="ow-logo-dark-ink h-[16px] w-auto" />
        </div>
        <select
          value={oddsFormat}
          onChange={handleOddsFormatChange}
          className="pl-2 pr-7 py-1 text-[8px] @md:text-xs font-medium bg-white border border-[var(--lp-accent,#0db5ff)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent @md:pl-3 @md:pr-8 appearance-none bg-no-repeat dark:bg-(--ow-inset) dark:focus:ring-(--ow-accent)"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundPosition: 'right 12px center' }}
        >
          <option value="american">American</option>
          <option value="decimal">Decimal</option>
        </select>
      </div>

      <ResponsibleGamblingFooter league={league} presentSportsbooks={presentSportsbooks} />
    </div>
  )
}

export default Widget
